import { LABELS, todayInTimezone } from '@mm/shared';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { CheckSquare, Lock, Plus } from 'lucide-react';
import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api, ApiError } from '../../api/client';
import { AppIcon } from '../../components/AppIcon';
import { DateRangeFilter, useDateFilter } from '../../components/DateRangeFilter';
import { ExportButton } from '../../components/ExportButton';
import { Button } from '../../components/ui/Button';
import { Alert, Badge, Card, EmptyState, PageHeader, Spinner } from '../../components/ui/display';
import { Field, Input, Select, Textarea } from '../../components/ui/inputs';
import { ConfirmDialog, Modal } from '../../components/ui/Modal';
import { SearchableSelect } from '../../components/ui/SearchableSelect';
import { Tabs } from '../../components/ui/Tabs';
import { useToast } from '../../components/ui/Toast';
import { DataTable } from '../../tables/DataTable';
import { formatDate, formatMoney, formatPercent, monthName } from '../../utils/format';
import { useAuth } from '../auth/AuthProvider';
import { ItcBadge } from '../marketing/MarketingPage';
import { sources } from '../masters/sources';

// ── API shapes ───────────────────────────────────────────────────────

interface Buckets {
  total: string;
  eligible: string;
  claimed: string;
  remaining: string;
  notEligible: string;
}
type GstAccountRef = { id: string; gstin: string; legalName: string; tradeName: string | null };

interface GstSummary {
  totals: Buckets;
  bySource: { marketing: Buckets; other: Buckets };
  months: (Buckets & { month: string })[];
  accounts: (Buckets & { gstAccount: (GstAccountRef & { state: string }) | null })[];
}

interface GstRow {
  source: 'MARKETING' | 'OTHER';
  id: string;
  transactionDate: string;
  description: string;
  app: { id: string; name: string; iconUrl: string | null } | null;
  gstAccount: GstAccountRef | null;
  amountBeforeGstInr: string;
  gstRate: string;
  gstAmountInr: string;
  totalAmountInr: string;
  itcStatus: keyof typeof LABELS.itcStatus;
  claimPeriod: {
    id: string;
    year: number;
    month: number;
    status: keyof typeof LABELS.claimPeriodStatus;
  } | null;
  itcClaimDate: string | null;
  claimReference: string | null;
  vendorName: string | null;
  invoiceNumber: string | null;
}

interface ClaimPeriod {
  id: string;
  gstAccountId: string;
  gstAccount: GstAccountRef;
  year: number;
  month: number;
  status: keyof typeof LABELS.claimPeriodStatus;
  totalItc: string;
  eligibleItc: string;
  claimedItc: string;
  pendingItc: string;
  live: boolean;
  linkedRows: number;
  notes: string | null;
  updatedAt: string;
}

const STATUS_TONE = {
  OPEN: 'blue',
  SUBMITTED: 'amber',
  CLAIMED: 'violet',
  CLOSED: 'slate',
} as const;
const ym = (m: string) => `${monthName(Number(m.slice(5, 7)))} ${m.slice(0, 4)}`;
const accountName = (a: GstAccountRef | null) =>
  a ? (a.tradeName ?? a.legalName) : 'No GST account';

function errorText(err: unknown) {
  return err instanceof ApiError ? (err.errors[0]?.message ?? err.message) : 'Request failed';
}

// ── Page ─────────────────────────────────────────────────────────────

/** GST dashboard: overview, transactions (bulk ITC status) and monthly claim periods. */
export function GstPage({
  defaultTab = 'overview',
}: {
  defaultTab?: 'overview' | 'transactions' | 'claims';
}) {
  const [params, setParams] = useSearchParams();
  const [range, setRange] = useDateFilter('thisMonth');
  const tab = (params.get('tab') as 'overview' | 'transactions' | 'claims' | null) ?? defaultTab;
  const gstAccountId = params.get('gstAccountId') ?? '';
  const set = (key: string, value: string) => {
    const p = new URLSearchParams(params);
    if (value) p.set(key, value);
    else p.delete(key);
    setParams(p, { replace: true });
  };

  return (
    <>
      <PageHeader
        title="GST"
        description="GST on marketing and other expenses is tracked apart from the expense. Eligible input credit is claimed monthly per GST account."
        actions={
          <>
            <SearchableSelect
              ariaLabel="GST account"
              placeholder="All GST accounts"
              clearable
              className="w-64"
              value={gstAccountId}
              remote={sources.gstAccount({})}
              onChange={(v) => set('gstAccountId', v)}
            />
            {tab !== 'claims' && <DateRangeFilter value={range} onChange={setRange} />}
          </>
        }
      />
      <Tabs
        tabs={[
          { id: 'overview', label: 'Overview' },
          { id: 'transactions', label: 'Transactions' },
          { id: 'claims', label: 'Claim periods' },
        ]}
        active={tab}
        onChange={(id) => set('tab', id)}
      />
      <div className="mt-6">
        {tab === 'overview' && (
          <Overview
            startDate={range.startDate}
            endDate={range.endDate}
            gstAccountId={gstAccountId}
            onAccount={(id) => set('gstAccountId', id)}
          />
        )}
        {tab === 'transactions' && (
          <Transactions
            startDate={range.startDate}
            endDate={range.endDate}
            gstAccountId={gstAccountId}
          />
        )}
        {tab === 'claims' && <Claims gstAccountId={gstAccountId} />}
      </div>
    </>
  );
}

// ── Overview ─────────────────────────────────────────────────────────

function BucketCards({ b }: { b: Buckets }) {
  const items = [
    { label: 'Total GST', value: b.total, hint: 'Paid on marketing and other expenses' },
    { label: 'Eligible', value: b.eligible, hint: 'Input credit you can claim' },
    { label: 'Claimed', value: b.claimed, hint: 'Already claimed in returns' },
    { label: 'Remaining', value: b.remaining, hint: 'Eligible, not claimed yet' },
    {
      label: 'Not eligible',
      value: b.notEligible,
      hint: 'Shown separately; not a cost in operating profit',
    },
  ];
  return (
    <div className="mb-6 grid grid-cols-2 gap-3 lg:grid-cols-5">
      {items.map((i) => (
        <div key={i.label} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
          <p className="text-xs font-medium text-slate-500">{i.label}</p>
          <p className="num mt-1 text-lg font-semibold text-violet-700">{formatMoney(i.value)}</p>
          <p className="mt-0.5 text-[11px] text-slate-400">{i.hint}</p>
        </div>
      ))}
    </div>
  );
}

const bucketColumns = <T extends Buckets>() => [
  {
    key: 'total',
    header: 'Total GST',
    align: 'right' as const,
    render: (r: T) => formatMoney(r.total),
  },
  {
    key: 'eligible',
    header: 'Eligible',
    align: 'right' as const,
    render: (r: T) => formatMoney(r.eligible),
  },
  {
    key: 'claimed',
    header: 'Claimed',
    align: 'right' as const,
    render: (r: T) => formatMoney(r.claimed),
  },
  {
    key: 'remaining',
    header: 'Remaining',
    align: 'right' as const,
    render: (r: T) => (
      <span className="font-medium text-violet-700">{formatMoney(r.remaining)}</span>
    ),
  },
  {
    key: 'notEligible',
    header: 'Not eligible',
    align: 'right' as const,
    render: (r: T) => formatMoney(r.notEligible),
  },
];

function Overview({
  startDate,
  endDate,
  gstAccountId,
  onAccount,
}: {
  startDate: string;
  endDate: string;
  gstAccountId: string;
  onAccount: (id: string) => void;
}) {
  const q = useQuery({
    queryKey: ['gst', 'summary', startDate, endDate, gstAccountId],
    queryFn: () =>
      api.get<GstSummary>('/gst/summary', {
        startDate,
        endDate,
        gstAccountId: gstAccountId || undefined,
      }),
    placeholderData: keepPreviousData,
  });
  const { can } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();
  const fill = useMutation({
    mutationFn: () =>
      api.post<{ updated: number; stillWithout: number }>('/gst/fill-gst-accounts', {
        startDate,
        endDate,
      }),
    onSuccess: (r) => {
      toast.success(
        r.updated
          ? `${r.updated} ads row(s) now on their ads account's GST account` +
              (r.stillWithout
                ? `; ${r.stillWithout} still without (their ads account has none)`
                : '')
          : r.stillWithout
            ? 'Nothing to fill: those ads accounts have no GST account yet. Set it on the ads account first.'
            : 'Nothing to fill',
      );
      void queryClient.invalidateQueries({ queryKey: ['gst'] });
      void queryClient.invalidateQueries({ queryKey: ['marketing'] });
    },
    onError: (err) => toast.error(errorText(err)),
  });
  const d = q.data;
  if (q.isError) return <Alert tone="error">{errorText(q.error)}</Alert>;
  if (!d) return <Spinner />;
  return (
    <>
      <div className="mb-3 flex justify-end">
        <ExportButton report="gst-summary" params={{ startDate, endDate, gstAccountId }} />
      </div>
      <BucketCards b={d.totals} />
      <div className="mb-6 grid grid-cols-1 gap-3 sm:grid-cols-2">
        <p className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm">
          Marketing GST{' '}
          <span className="num float-right font-medium">
            {formatMoney(d.bySource.marketing.total)}
          </span>
        </p>
        <p className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm">
          Other-expense GST{' '}
          <span className="num float-right font-medium">{formatMoney(d.bySource.other.total)}</span>
        </p>
      </div>
      <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
        <Card title="By month" bodyClassName="p-0">
          <DataTable
            tableId="gst"
            rows={d.months}
            rowKey={(r) => r.month}
            columns={[
              { key: 'month', header: 'Month', render: (r) => ym(r.month) },
              ...bucketColumns<(typeof d.months)[number]>(),
            ]}
            empty={<EmptyState title="No GST in this period" />}
          />
        </Card>
        <Card title="By GST account" bodyClassName="p-0">
          <DataTable
            tableId="gst-2"
            rows={d.accounts}
            rowKey={(r) => r.gstAccount?.id ?? 'none'}
            onRowClick={(r) => r.gstAccount && onAccount(r.gstAccount.id)}
            columns={[
              {
                key: 'account',
                header: 'GST account',
                render: (r) => (
                  <div className="min-w-[13rem]">
                    <p className="font-medium text-slate-900">{accountName(r.gstAccount)}</p>
                    {r.gstAccount ? (
                      <p className="font-mono text-xs text-slate-500">{r.gstAccount.gstin}</p>
                    ) : (
                      <>
                        <p className="text-xs text-amber-600">
                          Not claimable until assigned to a GST account (here, or select rows on the
                          Transactions tab).
                        </p>
                        {can('gst.manage') && (
                          <Button
                            size="sm"
                            variant="secondary"
                            className="mt-2 whitespace-nowrap"
                            loading={fill.isPending}
                            onClick={(e) => {
                              e.stopPropagation();
                              fill.mutate();
                            }}
                          >
                            Fill from ads accounts
                          </Button>
                        )}
                      </>
                    )}
                  </div>
                ),
              },
              ...bucketColumns<(typeof d.accounts)[number]>(),
            ]}
            empty={<EmptyState title="No GST in this period" />}
          />
        </Card>
      </div>
    </>
  );
}

// ── Transactions ─────────────────────────────────────────────────────

function Transactions({
  startDate,
  endDate,
  gstAccountId,
}: {
  startDate: string;
  endDate: string;
  gstAccountId: string;
}) {
  const { can } = useAuth();
  const canManage = can('gst.manage');
  const toast = useToast();
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [itcStatus, setItcStatus] = useState('');
  const [source, setSource] = useState('');
  const [selected, setSelected] = useState<Map<string, GstRow>>(new Map());
  const [bulk, setBulk] = useState<'ELIGIBLE' | 'NOT_ELIGIBLE' | 'CLAIMED' | null>(null);
  const [assigning, setAssigning] = useState(false);
  const filters = { startDate, endDate, gstAccountId, itcStatus, source };
  const q = useQuery({
    queryKey: ['gst', 'transactions', filters, page],
    queryFn: () => api.list<GstRow>('/gst/transactions', { ...filters, page, pageSize: 50 }),
    placeholderData: keepPreviousData,
  });
  const rows = q.data?.data ?? [];
  const key = (r: GstRow) => `${r.source}:${r.id}`;
  const lockedRow = (r: GstRow) => !!r.claimPeriod && r.claimPeriod.status !== 'OPEN';
  const toggle = (r: GstRow) =>
    setSelected((s) => {
      const n = new Map(s);
      if (n.has(key(r))) n.delete(key(r));
      else n.set(key(r), r);
      return n;
    });
  const selectable = rows.filter((r) => !lockedRow(r));
  const allOnPage = selectable.length > 0 && selectable.every((r) => selected.has(key(r)));

  return (
    <>
      <div className="mb-3 flex flex-wrap items-center gap-3">
        <div className="w-56">
          <Select
            aria-label="ITC status"
            value={itcStatus}
            onChange={(e) => {
              setItcStatus(e.target.value);
              setPage(1);
            }}
          >
            <option value="">All ITC statuses</option>
            {(['ELIGIBLE', 'CLAIMED', 'NOT_ELIGIBLE'] as const).map((s) => (
              <option key={s} value={s}>
                {LABELS.itcStatus[s]}
              </option>
            ))}
          </Select>
        </div>
        <div className="w-56">
          <Select
            aria-label="Source"
            value={source}
            onChange={(e) => {
              setSource(e.target.value);
              setPage(1);
            }}
          >
            <option value="">Marketing and other</option>
            <option value="MARKETING">Marketing</option>
            <option value="OTHER">Other expenses</option>
          </Select>
        </div>
        <span className="ml-auto">
          <ExportButton report="gst-transactions" params={filters} />
        </span>
      </div>

      {canManage && selected.size > 0 && (
        <div className="sticky top-16 z-10 mb-3 flex flex-wrap items-center gap-2 rounded-xl border border-brand-200 bg-brand-50 px-4 py-2 text-sm">
          <CheckSquare className="size-4 text-brand-700" />
          <span className="font-medium text-brand-900">{selected.size} selected</span>
          <span className="text-brand-800">→ mark as</span>
          <Button size="sm" variant="secondary" onClick={() => setBulk('ELIGIBLE')}>
            Eligible
          </Button>
          <Button size="sm" variant="secondary" onClick={() => setBulk('NOT_ELIGIBLE')}>
            Not eligible
          </Button>
          <Button size="sm" variant="secondary" onClick={() => setBulk('CLAIMED')}>
            Claimed…
          </Button>
          <span className="mx-1 text-brand-300">|</span>
          <Button size="sm" variant="secondary" onClick={() => setAssigning(true)}>
            Assign GST account…
          </Button>
          <button
            type="button"
            className="ml-auto text-xs text-brand-700 hover:underline"
            onClick={() => setSelected(new Map())}
          >
            Clear selection
          </button>
        </div>
      )}

      <Card bodyClassName="p-0">
        <DataTable<GstRow>
          tableId="gst-3"
          rows={rows}
          rowKey={key}
          loading={q.isLoading}
          meta={q.data?.meta}
          onPageChange={setPage}
          columns={[
            ...(canManage
              ? [
                  {
                    key: '__select',
                    header: (
                      <input
                        type="checkbox"
                        aria-label="Select all on this page"
                        checked={allOnPage}
                        onChange={() =>
                          setSelected((s) => {
                            const n = new Map(s);
                            for (const r of selectable) {
                              if (allOnPage) n.delete(key(r));
                              else n.set(key(r), r);
                            }
                            return n;
                          })
                        }
                      />
                    ),
                    render: (r: GstRow) =>
                      lockedRow(r) ? (
                        <Lock
                          className="size-3.5 text-slate-400"
                          aria-label="Locked by a claim period"
                        />
                      ) : (
                        <input
                          type="checkbox"
                          aria-label="Select row"
                          checked={selected.has(key(r))}
                          onChange={() => toggle(r)}
                        />
                      ),
                  },
                ]
              : []),
            {
              key: 'date',
              header: 'Date',
              render: (r) => (
                <span className="whitespace-nowrap">{formatDate(r.transactionDate)}</span>
              ),
            },
            {
              key: 'what',
              header: 'Expense',
              render: (r) => (
                <div>
                  <p className="text-slate-900">{r.description}</p>
                  <p className="flex items-center gap-1 text-xs text-slate-400">
                    {r.app ? (
                      <>
                        <AppIcon src={r.app.iconUrl} name={r.app.name} size={14} /> {r.app.name}
                      </>
                    ) : (
                      'Company-level'
                    )}
                    {r.source === 'OTHER' ? ' · other expense' : ''}
                  </p>
                </div>
              ),
            },
            {
              key: 'gstAccount',
              header: 'GST account',
              render: (r) =>
                r.gstAccount ? (
                  <span className="font-mono text-xs">{r.gstAccount.gstin}</span>
                ) : (
                  <span className="text-xs text-amber-600">None</span>
                ),
            },
            {
              key: 'before',
              header: 'Before GST',
              align: 'right',
              render: (r) => formatMoney(r.amountBeforeGstInr),
            },
            {
              key: 'rate',
              header: 'Rate',
              align: 'right',
              render: (r) => formatPercent(r.gstRate),
            },
            {
              key: 'gst',
              header: 'GST',
              align: 'right',
              render: (r) => <span className="text-violet-700">{formatMoney(r.gstAmountInr)}</span>,
            },
            { key: 'itc', header: 'ITC', render: (r) => <ItcBadge status={r.itcStatus} /> },
            {
              key: 'claim',
              header: 'Claim',
              render: (r) =>
                r.claimPeriod ? (
                  <span className="text-xs">
                    {monthName(r.claimPeriod.month)} {r.claimPeriod.year}{' '}
                    <Badge tone={STATUS_TONE[r.claimPeriod.status]}>
                      {LABELS.claimPeriodStatus[r.claimPeriod.status]}
                    </Badge>
                    {r.claimReference && (
                      <span className="block text-slate-400">{r.claimReference}</span>
                    )}
                  </span>
                ) : (
                  <span className="text-slate-300">—</span>
                ),
            },
            {
              key: 'invoice',
              header: 'Invoice',
              render: (r) => r.invoiceNumber ?? <span className="text-slate-300">—</span>,
            },
          ]}
          empty={<EmptyState title="No GST rows" description="Change the period or filters." />}
        />
      </Card>

      {assigning && (
        <AssignGstModal
          rows={[...selected.values()]}
          onClose={() => setAssigning(false)}
          onDone={(updated) => {
            toast.success(`${updated} row(s) moved to the GST account`);
            setSelected(new Map());
            setAssigning(false);
            void queryClient.invalidateQueries({ queryKey: ['gst'] });
            void queryClient.invalidateQueries({ queryKey: ['marketing'] });
            void queryClient.invalidateQueries({ queryKey: ['expenses'] });
          }}
        />
      )}
      {bulk && (
        <BulkModal
          status={bulk}
          rows={[...selected.values()]}
          onClose={() => setBulk(null)}
          onDone={(updated) => {
            toast.success(`${updated} row(s) updated`);
            setSelected(new Map());
            setBulk(null);
            void queryClient.invalidateQueries({ queryKey: ['gst'] });
            void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
          }}
        />
      )}
    </>
  );
}

/** Put the selected rows on one GST account (rows saved before the account had one). */
function AssignGstModal({
  rows,
  onClose,
  onDone,
}: {
  rows: GstRow[];
  onClose: () => void;
  onDone: (updated: number) => void;
}) {
  const [gstAccountId, setGstAccountId] = useState('');
  const [error, setError] = useState<string | null>(null);
  const total = rows.reduce((s, r) => s + Number(r.gstAmountInr), 0);
  const save = useMutation({
    mutationFn: () =>
      api.post<{ updated: number }>('/gst/gst-account', {
        items: rows.map((r) => ({ source: r.source, id: r.id })),
        gstAccountId,
      }),
    onSuccess: (r) => onDone(r.updated),
    onError: (err) => setError(errorText(err)),
  });
  return (
    <Modal
      open
      size="sm"
      onClose={onClose}
      title={`Assign ${rows.length} row(s) to a GST account`}
      description={`GST ${formatMoney(total.toFixed(2))}. The amounts stay the same; each change is recorded in the audit log.`}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button loading={save.isPending} disabled={!gstAccountId} onClick={() => save.mutate()}>
            Assign
          </Button>
        </>
      }
    >
      <Field label="GST account" htmlFor="assign-gst" required>
        <SearchableSelect
          inputId="assign-gst"
          value={gstAccountId}
          onChange={(v) => setGstAccountId(v ?? '')}
          remote={sources.gstAccount()}
        />
      </Field>
      <p className="mt-3 text-xs text-slate-500">
        Rows in a submitted or claimed period cannot move. Ads spend under a manager account (MCC)
        stays on the MCC&apos;s GST account.
      </p>
      {error && (
        <p className="mt-3 text-sm text-rose-600" role="alert">
          {error}
        </p>
      )}
    </Modal>
  );
}

function BulkModal({
  status,
  rows,
  onClose,
  onDone,
}: {
  status: 'ELIGIBLE' | 'NOT_ELIGIBLE' | 'CLAIMED';
  rows: GstRow[];
  onClose: () => void;
  onDone: (updated: number) => void;
}) {
  const [claimDate, setClaimDate] = useState(todayInTimezone());
  const [reference, setReference] = useState('');
  const [error, setError] = useState<string | null>(null);
  const total = rows.reduce((s, r) => s + Number(r.gstAmountInr), 0);
  const save = useMutation({
    mutationFn: () =>
      api.post<{ updated: number }>('/gst/itc-status', {
        items: rows.map((r) => ({ source: r.source, id: r.id })),
        itcStatus: status,
        ...(status === 'CLAIMED' ? { itcClaimDate: claimDate, claimReference: reference } : {}),
      }),
    onSuccess: (r) => onDone(r.updated),
    onError: (err) => setError(errorText(err)),
  });
  return (
    <Modal
      open
      size="sm"
      onClose={onClose}
      title={`Mark ${rows.length} row(s) as ${LABELS.itcStatus[status].toLowerCase()}?`}
      description={`GST ${formatMoney(total.toFixed(2))}. Each change is recorded in the audit log.`}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button loading={save.isPending} onClick={() => save.mutate()}>
            Update
          </Button>
        </>
      }
    >
      {status === 'CLAIMED' ? (
        <div className="space-y-4">
          <p className="text-sm text-slate-600">
            For regular monthly claims, use a claim period instead: it freezes the totals.
          </p>
          <Field label="Claim date" htmlFor="bulk-date">
            <Input
              id="bulk-date"
              type="date"
              value={claimDate}
              max={todayInTimezone()}
              onChange={(e) => setClaimDate(e.target.value)}
            />
          </Field>
          <Field label="Return reference" htmlFor="bulk-ref">
            <Input
              id="bulk-ref"
              value={reference}
              placeholder="GSTR-3B ARN"
              onChange={(e) => setReference(e.target.value)}
            />
          </Field>
        </div>
      ) : (
        <p className="text-sm text-slate-600">
          The rows keep their amounts; only the ITC status changes.
        </p>
      )}
      {error && (
        <p className="mt-3 text-sm text-rose-600" role="alert">
          {error}
        </p>
      )}
    </Modal>
  );
}

// ── Claim periods ────────────────────────────────────────────────────

const ACTION_LABEL = {
  submit: 'Submit',
  claim: 'Mark claimed',
  close: 'Close',
  reopen: 'Reopen',
} as const;
const NEXT_ACTIONS: Record<ClaimPeriod['status'], (keyof typeof ACTION_LABEL)[]> = {
  OPEN: ['submit'],
  SUBMITTED: ['claim', 'reopen'],
  CLAIMED: ['close', 'reopen'],
  CLOSED: ['reopen'],
};

function Claims({ gstAccountId }: { gstAccountId: string }) {
  const { can } = useAuth();
  const canManage = can('gst.manage');
  const canReopen = can('gst.reopen');
  const toast = useToast();
  const queryClient = useQueryClient();
  const [creating, setCreating] = useState(false);
  const [acting, setActing] = useState<{
    period: ClaimPeriod;
    action: keyof typeof ACTION_LABEL;
  } | null>(null);
  const [deleting, setDeleting] = useState<ClaimPeriod | null>(null);
  const q = useQuery({
    queryKey: ['gst', 'claims', gstAccountId],
    queryFn: () =>
      api.get<ClaimPeriod[]>('/gst/claims', { gstAccountId: gstAccountId || undefined }),
  });
  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['gst'] });
  const remove = useMutation({
    mutationFn: (id: string) => api.delete(`/gst/claims/${id}`),
    onSuccess: () => {
      toast.success('Claim period deleted');
      setDeleting(null);
      refresh();
    },
    onError: (err) => {
      toast.error(errorText(err));
      setDeleting(null);
    },
  });

  return (
    <>
      <div className="mb-3 flex items-center justify-between gap-3">
        <p className="text-sm text-slate-500">
          Open → Submitted (rows and totals frozen) → Claimed (rows marked claimed) → Closed. Only
          an administrator can reopen.
        </p>
        {canManage && (
          <Button icon={<Plus className="size-4" />} onClick={() => setCreating(true)}>
            New claim period
          </Button>
        )}
      </div>
      <Card bodyClassName="p-0">
        <DataTable<ClaimPeriod>
          tableId="gst-4"
          rows={q.data}
          loading={q.isLoading}
          rowKey={(r) => r.id}
          columns={[
            {
              key: 'month',
              header: 'Month',
              render: (r) => (
                <span className="font-medium">
                  {monthName(r.month)} {r.year}
                </span>
              ),
            },
            {
              key: 'account',
              header: 'GST account',
              render: (r) => (
                <div>
                  <p>{accountName(r.gstAccount)}</p>
                  <p className="font-mono text-xs text-slate-500">{r.gstAccount.gstin}</p>
                </div>
              ),
            },
            {
              key: 'status',
              header: 'Status',
              render: (r) => (
                <span className="flex items-center gap-1.5">
                  <Badge tone={STATUS_TONE[r.status]}>{LABELS.claimPeriodStatus[r.status]}</Badge>
                  {r.live ? (
                    <span className="text-[11px] text-slate-400">live</span>
                  ) : (
                    <Lock className="size-3 text-slate-400" />
                  )}
                </span>
              ),
            },
            {
              key: 'total',
              header: 'Total GST',
              align: 'right',
              render: (r) => formatMoney(r.totalItc),
            },
            {
              key: 'eligible',
              header: 'Eligible',
              align: 'right',
              render: (r) => formatMoney(r.eligibleItc),
            },
            {
              key: 'claimed',
              header: 'Claimed',
              align: 'right',
              render: (r) => <span className="text-violet-700">{formatMoney(r.claimedItc)}</span>,
            },
            {
              key: 'pending',
              header: 'Pending',
              align: 'right',
              render: (r) => formatMoney(r.pendingItc),
            },
            {
              key: 'rows',
              header: 'Rows',
              align: 'right',
              render: (r) => (r.live ? '—' : r.linkedRows),
            },
            ...(canManage
              ? [
                  {
                    key: '__actions',
                    header: '',
                    align: 'right' as const,
                    render: (r: ClaimPeriod) => (
                      <span className="inline-flex gap-1">
                        {NEXT_ACTIONS[r.status]
                          .filter((a) => a !== 'reopen' || canReopen)
                          .map((a) => (
                            <Button
                              key={a}
                              size="sm"
                              variant={a === 'reopen' ? 'ghost' : 'secondary'}
                              onClick={() => setActing({ period: r, action: a })}
                            >
                              {ACTION_LABEL[a]}
                            </Button>
                          ))}
                        {r.status === 'OPEN' && (
                          <Button size="sm" variant="ghost" onClick={() => setDeleting(r)}>
                            Delete
                          </Button>
                        )}
                      </span>
                    ),
                  },
                ]
              : []),
          ]}
          empty={
            <EmptyState
              title="No claim periods yet"
              description={canManage ? 'Create one per GST account and month.' : undefined}
            />
          }
        />
      </Card>
      {creating && (
        <CreateClaimModal
          defaultAccountId={gstAccountId}
          onClose={() => setCreating(false)}
          onDone={() => {
            setCreating(false);
            refresh();
          }}
        />
      )}
      {acting && (
        <ClaimActionModal
          period={acting.period}
          action={acting.action}
          onClose={() => setActing(null)}
          onDone={() => {
            toast.success(
              `${monthName(acting.period.month)} ${acting.period.year}: ${ACTION_LABEL[acting.action].toLowerCase()} done`,
            );
            setActing(null);
            refresh();
            void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
          }}
        />
      )}
      <ConfirmDialog
        open={deleting !== null}
        title="Delete this claim period?"
        message="Only open periods can be deleted; no GST rows are changed."
        confirmLabel="Delete"
        loading={remove.isPending}
        onConfirm={() => deleting && remove.mutate(deleting.id)}
        onCancel={() => setDeleting(null)}
      />
    </>
  );
}

function CreateClaimModal({
  defaultAccountId,
  onClose,
  onDone,
}: {
  defaultAccountId: string;
  onClose: () => void;
  onDone: () => void;
}) {
  const today = todayInTimezone();
  const prev = new Date(`${today.slice(0, 7)}-01T00:00:00Z`);
  prev.setUTCMonth(prev.getUTCMonth() - 1);
  const [accountId, setAccountId] = useState(defaultAccountId);
  const [month, setMonth] = useState(prev.toISOString().slice(0, 7));
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: () =>
      api.post('/gst/claims', {
        gstAccountId: accountId,
        year: Number(month.slice(0, 4)),
        month: Number(month.slice(5, 7)),
        notes,
      }),
    onSuccess: onDone,
    onError: (err) => setError(errorText(err)),
  });
  return (
    <Modal
      open
      size="sm"
      onClose={onClose}
      title="New claim period"
      description="Totals stay live until you submit the period."
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            loading={save.isPending}
            disabled={!accountId || !month}
            onClick={() => save.mutate()}
          >
            Create
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Field label="GST account" htmlFor="claim-account" required>
          <SearchableSelect
            inputId="claim-account"
            value={accountId}
            remote={sources.gstAccount()}
            onChange={setAccountId}
          />
        </Field>
        <Field label="Month" htmlFor="claim-month" required>
          <Input
            id="claim-month"
            type="month"
            value={month}
            max={today.slice(0, 7)}
            onChange={(e) => setMonth(e.target.value)}
          />
        </Field>
        <Field label="Notes" htmlFor="claim-notes">
          <Textarea
            id="claim-notes"
            rows={2}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </Field>
        {error && (
          <p className="text-sm text-rose-600" role="alert">
            {error}
          </p>
        )}
      </div>
    </Modal>
  );
}

const ACTION_HELP = {
  submit:
    'Eligible GST rows of this month are linked to the period and frozen (amounts and ITC status can no longer change). Totals are frozen.',
  claim: 'All linked rows are marked Claimed with the date and return reference below.',
  close: 'The period is final.',
  reopen:
    'Claimed rows go back to Eligible and every row is released so it can be edited again. Recorded in the audit log.',
} as const;

function ClaimActionModal({
  period,
  action,
  onClose,
  onDone,
}: {
  period: ClaimPeriod;
  action: keyof typeof ACTION_LABEL;
  onClose: () => void;
  onDone: () => void;
}) {
  const [claimDate, setClaimDate] = useState(todayInTimezone());
  const [reference, setReference] = useState('');
  const [error, setError] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: () =>
      api.post(`/gst/claims/${period.id}/actions`, {
        action,
        updatedAt: period.updatedAt,
        ...(action === 'claim' ? { claimDate, claimReference: reference } : {}),
      }),
    onSuccess: onDone,
    onError: (err) => setError(errorText(err)),
  });
  return (
    <Modal
      open
      size="sm"
      onClose={onClose}
      title={`${ACTION_LABEL[action]}: ${monthName(period.month)} ${period.year}`}
      description={`${accountName(period.gstAccount)} · ${period.gstAccount.gstin}`}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant={action === 'reopen' ? 'danger' : 'primary'}
            loading={save.isPending}
            onClick={() => save.mutate()}
          >
            {ACTION_LABEL[action]}
          </Button>
        </>
      }
    >
      <p className={clsx('text-sm', action === 'reopen' ? 'text-rose-700' : 'text-slate-600')}>
        {ACTION_HELP[action]}
      </p>
      {action === 'claim' && (
        <div className="mt-4 space-y-4">
          <Field label="Claim date" htmlFor="claim-date">
            <Input
              id="claim-date"
              type="date"
              value={claimDate}
              max={todayInTimezone()}
              onChange={(e) => setClaimDate(e.target.value)}
            />
          </Field>
          <Field label="Return reference" htmlFor="claim-ref" help="e.g. GSTR-3B ARN">
            <Input
              id="claim-ref"
              value={reference}
              onChange={(e) => setReference(e.target.value)}
            />
          </Field>
        </div>
      )}
      {error && (
        <p className="mt-3 text-sm text-rose-600" role="alert">
          {error}
        </p>
      )}
    </Modal>
  );
}
